import {
    buildMockContextualChatAnswer,
    buildMockDraftResponse,
    buildMockExecutionPerformance,
    buildMockGenericChatAnswer,
    buildMockMessageSuggestion,
    buildMockSortingChatAnswer,
    mockChatHistory,
    mockClientsOverview,
    mockCommitments,
    mockOpportunities,
} from "./designMocks";

function pathOnly(endpoint: string): string {
    return endpoint.split("?")[0];
}

function parseJsonBody(options: RequestInit): Record<string, unknown> | undefined {
    if (!options.body || typeof options.body !== "string") return undefined;
    try {
        return JSON.parse(options.body) as Record<string, unknown>;
    } catch {
        return undefined;
    }
}

function parseQuestionFromEndpoint(endpoint: string): string | undefined {
    try {
        const query = endpoint.includes("?") ? endpoint.slice(endpoint.indexOf("?")) : "";
        const params = new URLSearchParams(query);
        const question = params.get("question");
        return question ? decodeURIComponent(question) : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Resolves API calls in design mode without network. Unknown routes return safe empty payloads.
 */
export async function resolveDesignMock(
    endpoint: string,
    options: RequestInit = {}
): Promise<unknown> {
    const path = pathOnly(endpoint);
    const method = (options.method ?? "GET").toUpperCase();

    if (path.includes("/objectives/customer_overview")) {
        return mockClientsOverview;
    }

    if (path.includes("/commitments/list_commitments")) {
        return mockCommitments;
    }

    if (path.includes("/objectives/list_objectives")) {
        return mockOpportunities;
    }

    if (path.includes("/management/execution_performance")) {
        const params = new URLSearchParams(endpoint.includes("?") ? endpoint.slice(endpoint.indexOf("?")) : "");
        const now = new Date().toISOString();
        return buildMockExecutionPerformance({
            start: params.get("start") ?? now,
            end: params.get("end") ?? now,
            includePending: params.get("include_pending") === "true",
        });
    }

    if (path.includes("/chat/get_user_history_msgs")) {
        return { history: mockChatHistory };
    }

    if (path.includes("/chat/run_qa")) {
        const question = parseQuestionFromEndpoint(endpoint);
        return buildMockGenericChatAnswer(question);
    }

    if (path.includes("/chat/run_contextualized_qa")) {
        const body = parseJsonBody(options);
        const question = typeof body?.question === "string" ? body.question : undefined;
        return buildMockContextualChatAnswer(question, body?.context);
    }

    if (path.includes("/chat/run_message_suggestion")) {
        const body = parseJsonBody(options);
        const context =
            typeof body?.context === "object" && body.context !== null
                ? (body.context as Record<string, unknown>)
                : body;
        return buildMockMessageSuggestion(context);
    }

    if (path.includes("/chat/set_message_feedback")) {
        return { ok: true };
    }

    if (path.includes("/chat/list_rankings_per_market")) {
        return { rankings: [] };
    }

    if (path.includes("/chat/get_ordering_properties_for_market")) {
        return { market: "", ordering: [], filtering: [] };
    }

    if (path.includes("/chat/set_sorting_products")) {
        const body = parseJsonBody(options);
        const rankingText =
            typeof body?.ranking_text === "string"
                ? body.ranking_text
                : typeof body?.user_reply === "string"
                  ? body.user_reply
                  : undefined;
        const step = typeof body?.step === "number" ? body.step : 0;
        const action = typeof body?.action === "string" ? body.action : undefined;
        return buildMockSortingChatAnswer(rankingText, { step, action });
    }

    if (path === "/objectives/draft" && method === "POST") {
        const body = parseJsonBody(options);
        return buildMockDraftResponse({
            title: typeof body?.title === "string" ? body.title : undefined,
            text: typeof body?.text === "string" ? body.text : undefined,
            endDate: typeof body?.endDate === "string" ? body.endDate : undefined,
        });
    }

    if (path === "/objectives/draft/regenerate" && method === "POST") {
        const body = parseJsonBody(options);
        return buildMockDraftResponse({
            text: typeof body?.text === "string" ? body.text : undefined,
        });
    }

    if (path === "/objectives/draft/commit" && method === "POST") {
        return { message: "Objetivo simulado gravado (modo design — sem persistência)." };
    }

    if (path.includes("/objectives/delete_objective")) {
        return { message: "Objetivo removido (simulado)." };
    }

    if (path.includes("/objectives/create_objective")) {
        return { message: "Objetivo criado (simulado)." };
    }

    if (path.includes("/commitments/update_commitment_status")) {
        return { ok: true };
    }

    if (path.includes("/commitments/generate_recommendation_products")) {
        return {
            response_json: {
                list_of_products: [
                    {
                        product: "CDB Banco Exemplo 2027",
                        market: "RENDA FIXA",
                        strategy: "POSFIXADO",
                        justification: "Alinhado ao perfil e à liquidez do cliente.",
                    },
                    {
                        product: "Fundo RF Ativo Design",
                        market: "FUNDOS",
                        strategy: "RETORNO ABSOLUTO MM",
                        justification: "Diversificação em pós-fixado com liquidez D+1.",
                    },
                ],
                scenario: "LIQUIDITY_REBALANCE",
            },
        };
    }

    console.warn(`[design-mode] No mock for ${method} ${endpoint}; returning {}.`);
    return {};
}
